/**
 * Groups feature — Zod schemas.
 *
 * Groups are named bundles of users referenced by application
 * access grants and conditional-access policy conditions. The
 * backing table is `scim_groups` — the "scim_" prefix is a legacy
 * naming artifact; the admin surface treats it as the canonical
 * groups table and both native-admin mutations and SCIM provisioning
 * write to it through the same RLS-enforced path.
 *
 * Wire shape mirrors `pkg/api/tenant/group.go` on the backend.
 */

import { z } from "zod";
import { membershipRuleSchema, membershipRulesSchema } from "./rules.generated";
export { membershipRuleSchema, membershipRulesSchema } from "./rules.generated";

export const groupStatusSchema = z.enum(["active", "deleted"]);
export type GroupStatus = z.infer<typeof groupStatusSchema>;

/** What the group is for. Distribution groups are refused application
 *  grants — the enforcement that makes the type a fact, not a label. */
export const groupTypeSchema = z.enum(["security", "distribution", "role"]);
export type GroupType = z.infer<typeof groupTypeSchema>;

export const groupsListSearchSchema = z.object({
  q: z.string().optional(),
  sortBy: z.enum(["display_name", "created_at", "updated_at"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type GroupsListSearch = z.infer<typeof groupsListSearchSchema>;

export const groupSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  display_name: z.string(),
  external_id: z.string().nullable(),
  // The identity source whose feed OWNS the group; null for console-created
  // groups and groups staged for adoption. With external_id it decides the
  // three states group-source.ts renders: local, staged, directory-owned.
  source_id: z.string().nullable(),
  status: groupStatusSchema,
  // The tenant's All Users group: every person joins it when created. The
  // server refuses rename and delete; membership edits are ordinary.
  is_all_users: z.boolean(),
  member_count: z.number().int().nonnegative().optional(),
  // Applications directly granted to this group. With member_count, the
  // blast radius the staging dialog restates before the admin authorizes a
  // directory adoption.
  application_grant_count: z.number().int().nonnegative().optional(),
  // Nesting-flattened count — direct members plus everyone in active
  // descendant groups. Present on the get-one response only.
  effective_member_count: z.number().int().nonnegative().optional(),
  group_type: groupTypeSchema,
  // Review cadence: past review_due_at the additive paths (member adds,
  // nesting, new grants) refuse until an attest.
  review_interval_days: z.number().int().positive().nullish(),
  last_attested_at: z.string().nullish(),
  review_due_at: z.string().nullish(),
  review_overdue: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Group = z.infer<typeof groupSchema>;

export const groupOwnerSchema = z.object({
  user_id: z.string(),
  created_at: z.string(),
  user_display_name: z.string().nullish(),
  user_primary_email: z.string().nullish(),
});
export type GroupOwner = z.infer<typeof groupOwnerSchema>;

export const groupOwnersResponseSchema = z.object({
  data: z.array(groupOwnerSchema),
});

export const groupsListResponseSchema = z.object({
  data: z.array(groupSchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    // Uniform with every other list response — nonnegative, not
    // positive. See applications/schemas.ts for the rationale.
    limit: z.number().int().nonnegative(),
  }),
});
export type GroupsListResponse = z.infer<typeof groupsListResponseSchema>;

export const groupMemberSchema = z.object({
  user_id: z.string(),
  created_at: z.string(),
  // Denormalized from the users table by the backend so the
  // member list renders human-readable values without an n+1
  // user lookup per row. Both nullable: if the user row was
  // hard-deleted but the membership row survived (FK is ON
  // DELETE CASCADE — shouldn't happen — but defence in depth),
  // the UI falls back to the UUID. The backend uses omitempty
  // on these fields when null; `.nullish()` accepts both
  // absent and null.
  user_display_name: z.string().nullish(),
  user_primary_email: z.string().nullish(),
});
export type GroupMember = z.infer<typeof groupMemberSchema>;

export const groupMembersResponseSchema = z.object({
  data: z.array(groupMemberSchema),
});

export const createGroupSchema = z.object({
  display_name: z.string().min(1, "Display name is required").max(80),
  external_id: z.string().max(255).optional(),
});
export type CreateGroupInput = z.infer<typeof createGroupSchema>;

export const updateGroupSchema = z.object({
  display_name: z.string().min(1).max(80).optional(),
  external_id: z.string().max(255).nullable().optional(),
  group_type: groupTypeSchema.optional(),
  // 7–365 sets the cadence; 0 clears it.
  review_interval_days: z.number().int().nonnegative().optional(),
});
export type UpdateGroupInput = z.infer<typeof updateGroupSchema>;

// ── Membership rules (P4 slice 0) ─────────────────────────────────────────

export const membershipRuleStatusSchema = z.enum(["draft", "active", "paused"]);
export type MembershipRuleStatus = z.infer<typeof membershipRuleStatusSchema>;

export type MembershipRule = z.infer<typeof membershipRuleSchema>;

export const membershipRulePreviewSchema = z.object({
  digest: z.string(),
  adds: z.number().int().nonnegative(),
  removes: z.number().int().nonnegative(),
  add_sample: z.array(z.string()),
  remove_sample: z.array(z.string()),
  resulting_size: z.number().int().nonnegative(),
  evaluated: z.number().int().nonnegative(),
  eval_errors: z.number().int().nonnegative(),
  blast_radius_threshold: z.number().int().positive(),
});
export type MembershipRulePreview = z.infer<typeof membershipRulePreviewSchema>;

// ── Nesting (P4 slice 1) ──────────────────────────────────────────────────

export const groupNestingRefSchema = z.object({
  group_id: z.string(),
  display_name: z.string(),
  created_at: z.string(),
});
export type GroupNestingRef = z.infer<typeof groupNestingRefSchema>;

export const groupNestingsSchema = z.object({
  // Children are nested UNDER this group: their people are effectively
  // members here. Parents are the groups this one is nested under.
  children: z.array(groupNestingRefSchema),
  parents: z.array(groupNestingRefSchema),
});
export type GroupNestings = z.infer<typeof groupNestingsSchema>;

export const nestPreviewSchema = z.object({
  people_gaining: z.number().int().nonnegative(),
  total_people_gaining: z.number().int().nonnegative(),
  ancestors_affected: z.number().int().nonnegative(),
  application_grant_count: z.number().int(),
  blast_radius_threshold: z.number().int().positive(),
  // Computed server-side: the console never re-derives the rule.
  requires_acknowledgement: z.boolean(),
});
export type NestPreview = z.infer<typeof nestPreviewSchema>;

export const ruleInventorySearchSchema = z.object({ cursor: z.uuid().optional() });
export type RuleInventorySearch = z.infer<typeof ruleInventorySearchSchema>;
export type MembershipRules = z.infer<typeof membershipRulesSchema>;
